import { useState } from "react";
import { INTENSITY_MAX, INTENSITY_MIN } from "@/lib/solution-filters";

export function FilterSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-border py-2.5 first:pt-0 last:border-b-0">
      <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      {children}
    </div>
  );
}

export function ChipToggle({
  active,
  onToggle,
  children,
}: {
  active: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`rounded-full border px-2.5 py-0.5 text-sm font-medium transition ${
        active
          ? "border-foreground/40 bg-foreground text-background"
          : "border-border bg-card text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

export function ChipRow({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap gap-1.5">{children}</div>;
}

export function IntensityRange({
  min,
  max,
  onChange,
}: {
  min: number;
  max: number;
  onChange: (min: number, max: number) => void;
}) {
  const levels = Array.from(
    { length: INTENSITY_MAX - INTENSITY_MIN + 1 },
    (_, i) => INTENSITY_MIN + i,
  );
  const selectCls = "h-9 rounded-md border border-border bg-card px-2 text-sm outline-none";
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <select
        className={selectCls}
        value={min}
        onChange={(e) => {
          const lo = Number(e.target.value);
          onChange(lo, Math.max(lo, max));
        }}
      >
        {levels.map((l) => (
          <option key={l} value={l}>
            L{l}
          </option>
        ))}
      </select>
      <span>to</span>
      <select
        className={selectCls}
        value={max}
        onChange={(e) => {
          const hi = Number(e.target.value);
          onChange(Math.min(min, hi), hi);
        }}
      >
        {levels.map((l) => (
          <option key={l} value={l}>
            L{l}
          </option>
        ))}
      </select>
    </div>
  );
}

export function SearchableMultiSelect({
  options,
  selected,
  onToggle,
  placeholder,
}: {
  options: Array<{ id: string; name: string }>;
  selected: string[];
  onToggle: (id: string) => void;
  placeholder: string;
}) {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const shown = query ? options.filter((o) => o.name.toLowerCase().includes(query)) : options;
  return (
    <div>
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder}
        className="mb-1.5 h-9 w-full rounded-md border border-border bg-card px-2 text-sm outline-none focus:border-foreground/30"
      />
      <div className="max-h-40 overflow-y-auto">
        {shown.length === 0 && (
          <div className="px-1 py-2 text-sm text-muted-foreground">No matches.</div>
        )}
        {shown.map((o) => {
          const on = selected.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onToggle(o.id)}
              className={`flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-sm transition hover:bg-muted/60 ${
                on ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              <span
                className={`flex h-3.5 w-3.5 items-center justify-center rounded border text-xs ${
                  on ? "border-foreground bg-foreground text-background" : "border-border"
                }`}
              >
                {on ? "✓" : ""}
              </span>
              <span className="truncate">{o.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
